import { useEffect, useState } from "react";
import { useLoanDashboard } from "../../../contexts";
import { getVendorTitle } from "../../LoanApplicationSteps/VendorInformation/utils";
import {
  useGetApplicationVendors,
  useVendorManagementActions,
} from "../../../hooks";
import {
  LoanProductType,
  LoanVendorManagementAction,
} from "../../../hooks/api/types";
import { ButtonLoaderIcon, PlusIcon } from "../../../assets/icons";
import { Button } from "../../../components";

import VendorBankStatementSection from "./VendorBankStatementSection";
import NewLoanVendorsSection from "./NewLoanVendorsSection";
import VendorInformationModal, {
  AddVendorModalVariant,
} from "../../LoanApplicationSteps/VendorInformation/VendorInformationModal";
import {
  SubmissionSuccessView,
  RejectedVendorsSection,
  ActionRequiredSection,
  VendorListSection,
  SubmissionButtonsSection,
  PageState,
  categorizeVendors,
  getVendorDocumentRequestStatus,
} from "./VendorManagement";

const VendorManagementSection = () => {
  const [currentLoan, setCurrentLoan] = useState<any>();
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [pageState, setPageState] = useState<PageState>();
  const [submissionSuccessful, setSubmissionSuccessful] =
    useState<boolean>(false);
  const [isAddVendorModalOpen, setIsAddVendorModalOpen] =
    useState<boolean>(false);
  const [continuingWithoutPending, setContinuingWithoutPending] =
    useState<boolean>(false);
  const [submittingDocuments, setSubmittingDocuments] =
    useState<boolean>(false);

  const {
    state: { loans, currentLoanApplicationId: loanId },
    actions: { update, refetch },
  } = useLoanDashboard();

  const {
    data,
    isLoading,
    refetch: refetchVendors,
  } = useGetApplicationVendors({
    loanApplicationId: loanId,
    disablePagination: true,
  });

  const { mutateAsync: finalizeVendorManagement } =
    useVendorManagementActions();
  const vendorTitle = getVendorTitle(currentLoan?.loanType as LoanProductType);

  useEffect(() => {
    const loan = loans.find((loan) => loan.id === loanId);
    setCurrentLoan(loan);
  }, [loanId]);

  useEffect(() => {
    if (data && !isLoading) {
      setPageLoading(true);

      const { approved, draft, pending, rejected, pendingReview } =
        categorizeVendors(data?.data || []);

      const {
        vendorsPendingDocs,
        pendingBankStatementVendors,
        informationUploaded,
      } = getVendorDocumentRequestStatus(pending || []);

      setPageState({
        readyForSubmission: Boolean(draft?.length) || informationUploaded,
        isSubmitted: !(pending?.length || draft?.length),
        pendingVendors: vendorsPendingDocs,
        approvedVendors: approved,
        draftVendors: draft,
        rejectedVendors: rejected,
        pendingBankStatementVendors,
        pendingReviewVendors: pendingReview,
      });
      setPageLoading(false);
    }
  }, [data, isLoading]);

  const handleGoBack = async () => {
    refetch();
    await update({
      currentLoanApplicationId: undefined,
      showFacilityOverview: false,
    });
  };

  const handleContinueWithoutPending = async () => {
    setContinuingWithoutPending(true);
    try {
      await finalizeVendorManagement({
        loanApplicationId: loanId,
        type: LoanVendorManagementAction.CONTINUE_WITHOUT_PENDING_VENDORS,
      });
      await refetchVendors();
    } finally {
      setContinuingWithoutPending(false);
    }
  };

  const handleSubmitDocuments = async () => {
    setSubmittingDocuments(true);
    try {
      await finalizeVendorManagement({
        loanApplicationId: loanId,
        type: LoanVendorManagementAction.SUBMIT_DOCUMENTS,
      });
      await refetchVendors();
      setSubmissionSuccessful(true);
    } finally {
      setSubmittingDocuments(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="cx-flex cx-flex-col cx-items-center cx-w-full">
        <div className="cx-animate-spin cx-w-8 cx-h-8 cx-text-brand-primary-regular">
          <ButtonLoaderIcon />
        </div>
      </div>
    );
  }

  const showRejectedSection =
    !(
      pageState?.approvedVendors.length ||
      pageState?.pendingVendors.length ||
      pageState?.pendingBankStatementVendors.length
    ) && Boolean(pageState?.rejectedVendors.length);

  return (
    <div className="cx-grow cx-flex cx-flex-col cx-content-center cx-items-center cx-w-full">
      <VendorInformationModal
        isOpen={isAddVendorModalOpen}
        loanState={currentLoan}
        onClose={() => setIsAddVendorModalOpen(false)}
        onAddVendor={async () => {
          setIsAddVendorModalOpen(false);
          refetchVendors();
        }}
        vendorState={undefined}
        variant={AddVendorModalVariant.ALL_MANDATORY}
      />

      {submissionSuccessful ? (
        <SubmissionSuccessView onGoBack={handleGoBack} />
      ) : (
        <div className="cx-py-6 cx-w-full cx-flex cx-flex-col">
          <div className="cx-flex cx-justify-between cx-items-center cx-mb-4">
            <div className="cx-font-bold cx-text-xl">{vendorTitle}s</div>
            <Button
              label={
                <div className="cx-flex cx-items-center">
                  <PlusIcon className="cx-mr-2" />
                  <div>Add {vendorTitle}</div>
                </div>
              }
              slim={true}
              onClick={() => setIsAddVendorModalOpen(true)}
            />
          </div>

          {showRejectedSection && (
            <RejectedVendorsSection loanType={currentLoan?.loanType} />
          )}

          {Boolean(pageState?.pendingVendors?.length) && (
            <ActionRequiredSection
              vendors={pageState?.pendingVendors || []}
              loanApplication={currentLoan}
              loanType={currentLoan?.loanType}
              onSuccess={refetchVendors}
            />
          )}

          {Boolean(pageState?.pendingReviewVendors?.length) && (
            <VendorListSection
              title="Pending"
              vendors={pageState?.pendingReviewVendors || []}
              loanType={currentLoan?.loanType}
              showLicenseNumber
            />
          )}

          {Boolean(pageState?.approvedVendors?.length) && (
            <VendorListSection
              title="Active"
              vendors={pageState?.approvedVendors || []}
              loanType={currentLoan?.loanType}
            />
          )}

          {Boolean(pageState?.pendingBankStatementVendors?.length) && (
            <VendorBankStatementSection
              vendors={pageState?.pendingBankStatementVendors}
              loanApplicationId={loanId}
              onSuccess={refetchVendors}
            />
          )}

          {Boolean(pageState?.draftVendors?.length) && (
            <NewLoanVendorsSection
              loanApplication={currentLoan}
              vendors={pageState?.draftVendors}
              onSuccess={refetchVendors}
            />
          )}

          {!pageState?.isSubmitted &&
            (Boolean(pageState?.pendingVendors?.length) ||
              Boolean(pageState?.pendingBankStatementVendors?.length)) && (
              <SubmissionButtonsSection
                hasApprovedVendors={Boolean(pageState?.approvedVendors?.length)}
                readyForSubmission={pageState?.readyForSubmission || false}
                continuingWithoutPending={continuingWithoutPending}
                submittingDocuments={submittingDocuments}
                loanId={loanId}
                onContinueWithoutPending={handleContinueWithoutPending}
                onSubmitDocuments={handleSubmitDocuments}
              />
            )}
        </div>
      )}
    </div>
  );
};

export default VendorManagementSection;
